import { useFormContext } from '@calendly/form'
import { I18n } from '@calendly/i18n'
import { Button } from '@calendly/ui/components/button'
import { IconTrash } from '@calendly/ui/components/glyphs/IconTrash'
import { IconButton } from '@calendly/ui/components/icon-button'
import { DragIcon } from '@calendly/ui/components/icons'
import { LabelText } from '@calendly/ui/components/typography'
import { colorIconSecondary } from '@calendly/ui/theme'
import { Formik } from 'formik'
import { useEffect, useMemo, useRef } from 'react'

import {
  getCouponFormInitialValues,
  CouponFormFields,
} from './create_coupon_modal'
import {
  InlineCouponCard,
  InlineCouponDragHandle,
  InlineCouponFooter,
  InlineCouponHeader,
  InlineCouponHeaderTitle,
} from './create_coupon_modal_styles'
import { buildValidationSchema } from './create_coupon_modal_utils'

// The inline coupon editor lives inside the event-type editor's own <form>. Rendering
// another <form> here would nest form elements (invalid HTML), which breaks submit in the
// browser. Instead we provide an isolated Formik context (no <form> element), submit via
// an explicit "Done" button, and intercept Enter so it does not save the parent editor.
const shouldIgnoreEnterSubmit = (target) => (
  Boolean(
    target.closest('button, textarea, a, [contenteditable="true"], [role="listbox"], [role="option"]')
  )
)

const InlineCouponFormBody = ({
  currency,
  isEditMode,
  couponToEdit,
  apiError,
  isLoading,
  onClose,
}) => {
  const { submitForm, isSubmitting } = useFormContext()
  const rootRef = useRef(null)

  useEffect(() => {
    const root = rootRef.current
    if (!root) return undefined

    const handleKeyDown = (event) => {
      if (event.key !== 'Enter' || event.defaultPrevented) return
      if (shouldIgnoreEnterSubmit(event.target)) return

      // Prevent the parent event-type <form> from submitting on Enter.
      event.preventDefault()
      submitForm()
    }

    root.addEventListener('keydown', handleKeyDown)
    return () => root.removeEventListener('keydown', handleKeyDown)
  }, [submitForm])

  return (
    <div ref={rootRef}>
      <CouponFormFields
        currency={currency}
        isEditMode={isEditMode}
        couponToEdit={couponToEdit}
        apiError={apiError}
        variant="inline"
      />
      <InlineCouponFooter>
        <Button
          appearance="ghost"
          size="small"
          type="button"
          onClick={onClose}
        >
          {I18n.t('publisher.action.cancel')}
        </Button>
        <Button
          appearance="primary"
          size="small"
          type="button"
          loading={isLoading || isSubmitting}
          onClick={submitForm}
          data-testid="coupon-inline-done"
        >
          {I18n.t('publisher.action.done')}
        </Button>
      </InlineCouponFooter>
    </div>
  )
}

export const CouponInlineForm = ({
  isOpen,
  isEditMode = false,
  couponToEdit = null,
  apiError,
  isLoading = false,
  isDeleting = false,
  onClose,
  onSubmit,
  onDelete,
}) => {
  const { values: { currency } } = useFormContext()
  const validationSchema = useMemo(
    () => buildValidationSchema({ useCrudValidationMessages: true }),
    []
  )

  if (!isOpen) {
    return null
  }

  const handleSubmit = async (formValues) => {
    if (onSubmit) {
      await onSubmit(formValues, currency, isEditMode, couponToEdit)
    }
  }

  const handleTrashClick = () => {
    if (isEditMode && couponToEdit?.uuid && onDelete) {
      onDelete(couponToEdit.uuid)
      return
    }
    onClose()
  }

  const title = isEditMode
    ? I18n.t('publisher.event_types.payment_method.edit_coupon_code')
    : I18n.t('publisher.event_type_editor_crud.payments.new_coupon_code')

  const trashLabel = isEditMode
    ? I18n.t('publisher.event_types.payment_method.delete_coupon')
    : I18n.t('publisher.action.delete')

  return (
    <InlineCouponCard data-testid="coupon-inline-form">
      <InlineCouponHeader>
        <InlineCouponDragHandle aria-hidden="true">
          <DragIcon color={colorIconSecondary} size="standard" title="" />
        </InlineCouponDragHandle>
        <InlineCouponHeaderTitle>
          <LabelText color="muted">{title}</LabelText>
        </InlineCouponHeaderTitle>
        <IconButton
          appearance="ghost"
          size="small"
          title={trashLabel}
          aria-label={trashLabel}
          onClick={handleTrashClick}
          icon={IconTrash}
          disabled={isEditMode && isDeleting}
          data-testid="coupon-inline-trash"
        />
      </InlineCouponHeader>

      <Formik
        key={isEditMode ? (couponToEdit?.uuid ?? 'edit') : 'create'}
        initialValues={getCouponFormInitialValues()}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={false}
      >
        <InlineCouponFormBody
          currency={currency}
          isEditMode={isEditMode}
          couponToEdit={couponToEdit}
          apiError={apiError}
          isLoading={isLoading}
          onClose={onClose}
        />
      </Formik>
    </InlineCouponCard>
  )
}
